import {
  ChangeDetectorRef,
  OnDestroy,
  Pipe,
  PipeTransform,
  inject
} from '@angular/core';
import { getThemeAwareColor } from '@bepatient/shared';
import { Subscription } from 'rxjs';
import { ThemeService } from '../services/theme.service';

/**
 * Render-time, theme-reactive study-color adjustment for template bindings.
 *
 * Usage:
 *   [style.color]="studyColor | themeColor"
 *   [style.background]="factsheet.color | themeColor: true"   // isFactsheet
 *
 * Runs the input color through the canonical `getThemeAwareColor`
 * (see docs/adr/0001-contrast-utility-consolidation.md) and — like AsyncPipe —
 * injects the host's ChangeDetectorRef and calls `markForCheck()` on every
 * theme toggle so OnPush components re-render with the freshly adjusted color
 * without a page reload (AC: "updates immediately on switch").
 *
 * Declared `pure: false` so `transform` is consulted each change-detection
 * cycle; results are memoised per (hex, isFactsheet) and invalidated on theme
 * change to avoid recomputation churn.
 */
@Pipe({
  name: 'themeColor',
  pure: false,
  standalone: false
})
export class ThemeColorPipe implements PipeTransform, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly themeService = inject(ThemeService);

  private readonly sub: Subscription;
  private lastHex: string | null = null;
  private lastFactsheet = false;
  private lastResult = '';
  private dirty = true;

  constructor() {
    this.sub = this.themeService.themeChange$.subscribe(() => {
      this.dirty = true;
      this.cdr.markForCheck();
    });
  }

  transform(hex: string, isFactsheet = false): string {
    if (!hex) {
      return hex;
    }
    if (
      this.dirty ||
      hex !== this.lastHex ||
      isFactsheet !== this.lastFactsheet
    ) {
      this.lastHex = hex;
      this.lastFactsheet = isFactsheet;
      this.lastResult = getThemeAwareColor(hex, isFactsheet);
      this.dirty = false;
    }
    return this.lastResult;
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
